import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { authAPI } from '../../utils/authAPI';
import CustomContext from '../../../custom-context';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';

const StyledModal = styled(Modal)`
	.modal-content {
		border-radius: 16px;
		border: none;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
		overflow: hidden;
	}

	.modal-header {
		border-bottom: 1px solid #f0f0f0;
		padding: 24px 32px 16px;
		background: ${(props) => props.brandColors?.primary || 'var(--primary)'};
		color: white;
	}

	.modal-title {
		font-weight: 700;
		font-size: 1.5rem;
		color: white;
	}

	.modal-body {
		padding: 32px;
		background: #fafbfc;
	}

	.modal-header .btn-close {
		filter: invert(1);
		opacity: 0.8;
	}

	.modal-header .btn-close:hover {
		opacity: 1;
	}
`;

const StepContainer = styled.div`
	position: relative;
	overflow: hidden;
`;

const StepContent = styled.div`
	transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	transform: translateX(
		${(props) =>
			props.slideDirection === 'left'
				? '-100%'
				: props.slideDirection === 'right'
				? '100%'
				: '0'}
	);
	opacity: ${(props) => (props.slideDirection ? '0' : '1')};
`;

const StepIndicator = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-bottom: 32px;
	gap: 8px;
`;

const StepDot = styled.div`
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: ${(props) =>
		props.active
			? props.brandColors?.secondary_1 || 'var(--secondary_1)'
			: props.completed
			? '#10b981'
			: '#e5e7eb'};
	transition: all 0.3s ease;
	position: relative;

	&::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: white;
		opacity: ${(props) => (props.active ? '1' : '0')};
		transition: opacity 0.3s ease;
	}
`;

const StepLine = styled.div`
	width: 40px;
	height: 2px;
	background: ${(props) => (props.completed ? '#10b981' : '#e5e7eb')};
	transition: background 0.3s ease;
`;

const FormContainer = styled.div`
	background: white;
	border-radius: 12px;
	padding: 32px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h4`
	font-weight: 700;
	color: #1f2937;
	margin-bottom: 8px;
	text-align: center;
	font-family: ${(props) => {
		const fontFamily = props.fontFamily?.hero_title_font_family;
		switch (fontFamily) {
			case 'raleway':
				return 'Raleway Variable, Arial, "Helvetica Neue", Helvetica, sans-serif';
			case 'montserrat':
				return 'Montserrat Variable, Arial, "Helvetica Neue", Helvetica, sans-serif';
			case 'heebo':
				return 'Heebo Variable, Arial, "Helvetica Neue", Helvetica, sans-serif';
			case 'boing':
				return 'Boing, Arial, "Helvetica Neue", Helvetica, sans-serif';
			case 'outfit':
				return 'Outfit Variable, Arial, "Helvetica Neue", Helvetica, sans-serif';
			default:
				return 'var(--hero_title_font_family)';
		}
	}};
`;

const FormSubtitle = styled.p`
	color: #6b7280;
	text-align: center;
	margin-bottom: 32px;
	font-size: 0.95rem;
	font-family: ${(props) => {
		const fontFamily = props.fontFamily?.primary_font_family;
		switch (fontFamily) {
			case 'raleway':
				return 'Raleway Variable, Arial, "Helvetica Neue", Helvetica, sans-serif';
			case 'montserrat':
				return 'Montserrat Variable, Arial, "Helvetica Neue", Helvetica, sans-serif';
			case 'heebo':
				return 'Heebo Variable, Arial, "Helvetica Neue", Helvetica, sans-serif';
			case 'boing':
				return 'Boing, Arial, "Helvetica Neue", Helvetica, sans-serif';
			case 'outfit':
				return 'Outfit Variable, Arial, "Helvetica Neue", Helvetica, sans-serif';
			default:
				return 'var(--primary_font_family)';
		}
	}};
`;

const StyledForm = styled(Form)`
	.form-label {
		font-weight: 600;
		color: #374151;
		margin-bottom: 8px;
		font-size: 0.9rem;
	}

	.form-control {
		border-radius: 8px;
		border: 2px solid #e5e7eb;
		padding: 12px 16px;
		font-size: 1rem;
		transition: all 0.2s ease;

		&:focus {
			border-color: ${(props) =>
				props.brandColors?.secondary_1 || 'var(--secondary_1)'};
			box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
		}
	}

	.form-text {
		font-size: 0.8rem;
		color: #6b7280;
		margin-top: 4px;
	}

	/* Ensure perfect alignment for name fields */
	.name-row {
		/* Just ensure alignment - no extra styling */
		.form-group {
			height: 100%;
		}

		.form-label {
			margin-bottom: 8px;
		}

		.form-control {
			min-height: 48px;
		}
	}

	/* Email validation status indicators */
	.position-relative {
		.form-control {
			padding-right: 3rem; /* Make room for the indicator */
		}
	}

	.position-absolute {
		z-index: 10;
	}

	/* Button layout consistency */
	.row.g-3 {
		margin-left: -0.5rem;
		margin-right: -0.5rem;
	}

	.row.g-3 > .col-6 {
		padding-left: 0.5rem;
		padding-right: 0.5rem;
	}

	/* Success modal styling */
	.text-muted {
		color: #6b7280 !important;
		font-size: 1rem;
		line-height: 1.5;
	}

	.fa-check-circle {
		color: #10b981 !important;
	}
`;

const StyledButton = styled(Button)`
	border-radius: ${(props) =>
		props.buttonShape === 'rectangle' ? '0' : '12px'} !important;
	padding: 12px 24px;
	font-weight: 600;
	font-size: 1rem;
	transition: all 0.2s ease;
	border: none;
	min-width: 140px;

	/* Ensure all button variants have consistent styling */
	&.btn-primary,
	&.btn-outline-secondary,
	&.btn-warning,
	&.btn-success {
		border-radius: ${(props) =>
			props.buttonShape === 'rectangle' ? '0' : '12px'} !important;
	}

	&.btn-primary {
		background: ${(props) => props.brandColors?.primary || 'var(--primary)'};
		color: white !important;

		&:hover {
			background: ${(props) => props.brandColors?.primary || 'var(--primary)'};
			color: white !important;
			transform: translateY(-1px);
			box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
		}

		&:focus {
			background: ${(props) => props.brandColors?.primary || 'var(--primary)'};
			color: white !important;
			box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.2);
		}

		&:active {
			background: ${(props) => props.brandColors?.primary || 'var(--primary)'};
			color: white !important;
			transform: translateY(0);
		}

		&:disabled {
			background: #9ca3af;
			color: white !important;
			transform: none;
			box-shadow: none;
		}
	}

	/* Outline button styling */
	&.btn-outline-secondary {
		border: 2px solid
			${(props) => props.brandColors?.secondary_1 || 'var(--secondary_1)'};
		color: ${(props) => props.brandColors?.secondary_1 || 'var(--secondary_1)'};
		background: transparent;

		&:hover {
			background: ${(props) =>
				props.brandColors?.secondary_1 || 'var(--secondary_1)'};
			color: white;
			border-color: ${(props) =>
				props.brandColors?.secondary_1 || 'var(--secondary_1)'};
		}
	}

	/* Outline primary button styling for institutional login */
	&.btn-outline-primary {
		border: 2px solid
			${(props) => props.brandColors?.secondary_1 || 'var(--secondary_1)'};
		color: ${(props) => props.brandColors?.secondary_1 || 'var(--secondary_1)'};
		background: transparent;

		&:hover {
			background: ${(props) =>
				props.brandColors?.secondary_1 || 'var(--secondary_1)'};
			color: white;
			border-color: ${(props) =>
				props.brandColors?.secondary_1 || 'var(--secondary_1)'};
		}
	}

	/* Warning button styling */
	&.btn-warning {
		background: #f59e0b;
		color: white;
		border: none;

		&:hover {
			background: #d97706;
			color: white;
		}
	}

	/* Success button styling */
	&.btn-success {
		background: #10b981;
		color: white;
		border: none;

		&:hover {
			background: #059669;
			color: white;
		}
	}
`;

const ImageStyledButton = styled(Button)`
	background: #1e1b4b;
	color: white;
	border: none;
	border-radius: ${(props) =>
		props.buttonShape === 'rectangle' ? '0' : '8px'};
	padding: 16px 24px;
	font-weight: 600;
	font-size: 1rem;
	width: 100%;

	&:hover {
		background: #2d2566;
		color: white;
	}

	&:focus {
		background: #2d2566;
		color: white;
		box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.2);
	}

	&:active {
		background: #1e1b4b;
		color: white;
	}
`;

const QRCodeContainer = styled.div`
	text-align: center;
	margin: 24px 0;

	img {
		border-radius: 12px;
		box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
		max-width: 200px;
	}
`;

const SecretCode = styled.div`
	background: #f3f4f6;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 12px;
	margin: 16px 0;
	text-align: center;

	code {
		font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
		font-size: 1.1rem;
		color: #374151;
		user-select: all;
	}
`;

const AlertStyled = styled(Alert)`
	border-radius: 8px;
	border: none;
	font-weight: 500;

	&.alert-success {
		background: #d1fae5;
		color: #065f46;
	}

	&.alert-danger {
		background: #fee2e2;
		color: #991b1b;
	}
`;

const AuthModal = ({
	show,
	onHide,
	mode,
	prefilledEmail,
	memberInstitutes,
	login,
}) => {
	// Get strapiAsset data for button shape, brand colors, and fonts
	const strapiAssetData = useStaticQuery(graphql`
		query AuthModalStrapiAsset {
			strapiAsset {
				button_shape
				brand_colours {
					primary
					secondary_1
					secondary_2
					secondary_6
				}
				font_family {
					primary_font_family
					hero_title_font_family
					alternative_font_family
				}
			}
		}
	`);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [loginStep, setLoginStep] = useState('email');
	const [slideDirection, setSlideDirection] = useState('');
	const [qrCode, setQrCode] = useState('');
	const [twoFactorSecret, setTwoFactorSecret] = useState('');
	const [joinStep, setJoinStep] = useState('form'); // New: join flow steps
	const [detectedMemberInstitute, setDetectedMemberInstitute] = useState(null);
	const [joinScenario, setJoinScenario] = useState(null); // 'active_institute', 'inactive_institute', 'individual'
	const [emailValidationStatus, setEmailValidationStatus] = useState('idle'); // 'idle', 'loading', 'validated'
	const [emailValidationTimeout, setEmailValidationTimeout] = useState(null);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [showAAFButton, setShowAAFButton] = useState(false);

	// Track password step mode (setup vs entry)
	const [passwordStepMode, setPasswordStepMode] = useState('setup'); // 'setup' or 'entry'

	// Track 2FA step mode (setup vs entry)
	const [twoFAMode, setTwoFAMode] = useState('setup'); // 'setup' or 'entry'

	const { user } = React.useContext(CustomContext);

	const [formData, setFormData] = useState({
		email: '',
		first_name: '',
		last_name: '',
		job_title: '',
		member_institute_id: '',
		password: '',
		confirmPassword: '',
		twoFactorCode: '',
	});

	// Handle prefilled email when modal opens in join mode
	useEffect(() => {
		if (show && mode === 'join' && prefilledEmail) {
			setFormData((prev) => ({
				...prev,
				email: prefilledEmail,
			}));
		}
	}, [show, mode, prefilledEmail]);

	// Reset form when modal opens/closes
	// useEffect(() => {
	// 	if (!show) {
	// 		resetForm();
	// 	}
	// }, [show]);

	const slideToStep = (newStep) => {
		setSlideDirection('left');
		setTimeout(() => {
			setLoginStep(newStep);
			setSlideDirection('right');
			setTimeout(() => {
				setSlideDirection('');
			}, 50);
		}, 200);
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		// Handle email input with delayed validation
		if (mode === 'join' && name === 'email') {
			// Clear any existing timeout
			if (emailValidationTimeout) {
				clearTimeout(emailValidationTimeout);
			}

			if (value.includes('@')) {
				// Show loading circle immediately
				setEmailValidationStatus('loading');

				// Set 2.5 second delay before validation
				const timeout = setTimeout(() => {
					validateEmailDomain(value);
				}, 2500);

				setEmailValidationTimeout(timeout);
			} else {
				// Reset status if email is incomplete
				setEmailValidationStatus('idle');
				setDetectedMemberInstitute(null);
				setJoinScenario(null);
			}
		}
	};

	const handleEmailBlur = async (e) => {
		const email = e.target.value;
		if (mode === 'join' && email.includes('@')) {
			try {
				// Don't set loading state during email validation to avoid interference
				setError('');

				const response = await authAPI.validateDomain(email);

				// Check if email already exists
				if (response.email_exists) {
					setDetectedMemberInstitute(response.member_institute || null);

					// Set appropriate scenario
					if (response.is_aaf_member) {
						setJoinScenario('email_exists_aaf');
					} else {
						setJoinScenario('email_exists_regular');
					}

					setEmailValidationStatus('validated');
					return;
				}

				if (response.valid) {
					setDetectedMemberInstitute(response.member_institute);

					// Auto-populate member institute ID
					setFormData((prev) => ({
						...prev,
						member_institute_id: response.member_institute.id.toString(),
					}));

					// Determine join scenario - Check AAF first, then active status
					if (response.member_institute.aaf_member) {
						setJoinScenario('aaf_member');
					} else if (response.member_institute.is_active) {
						setJoinScenario('active_institute');
					} else {
						setJoinScenario('inactive_institute');
					}
				} else {
					setJoinScenario('individual');
					setDetectedMemberInstitute(null);
				}

				// Set validation status to completed
				setEmailValidationStatus('validated');
			} catch (error) {
				console.error('Domain validation error:', error);
				setError('Failed to validate email domain. Please try again.');
				setEmailValidationStatus('idle');
			}
		}
	};

	const validateEmailDomain = async (email) => {
		try {
			console.log('Starting domain validation for:', email);
			const response = await authAPI.validateDomain(email);
			console.log('Domain validation response:', response);

			// Check if email already exists
			if (response.email_exists) {
				setDetectedMemberInstitute(response.member_institute || null);

				// Set appropriate scenario (error will be set by handleEmailBlur)
				if (response.is_aaf_member) {
					setJoinScenario('email_exists_aaf');
				} else {
					setJoinScenario('email_exists_regular');
				}

				setEmailValidationStatus('validated');
				return false; // Return false to indicate validation failed
			}

			if (response.valid) {
				setDetectedMemberInstitute(response.member_institute);

				// Auto-populate member institute ID
				setFormData((prev) => ({
					...prev,
					member_institute_id: response.member_institute.id.toString(),
				}));

				// Determine join scenario - Check AAF first, then active status
				if (response.member_institute.aaf_member) {
					setJoinScenario('aaf_member');
				} else if (response.member_institute.is_active) {
					setJoinScenario('active_institute');
				} else {
					setJoinScenario('inactive_institute');
				}
			} else {
				setJoinScenario('individual');
				setDetectedMemberInstitute(null);
			}

			// Set validation status to completed
			setEmailValidationStatus('validated');
			console.log(
				'Email validation completed. Scenario:',
				response.valid ? 'institute detected' : 'individual member'
			);
			return true;
		} catch (error) {
			console.error('Domain validation error:', error);
			// Don't show error to user - just log it and allow individual membership
			setJoinScenario('individual');
			setDetectedMemberInstitute(null);
			setEmailValidationStatus('validated');
			console.log(
				'Email validation completed with error. Scenario: individual member'
			);
			return true;
		}
	};

	const resetForm = () => {
		setFormData({
			email: '',
			first_name: '',
			last_name: '',
			job_title: '',
			member_institute_id: '',
			password: '',
			confirmPassword: '',
			twoFactorCode: '',
		});
		setLoginStep('email');
		setJoinStep('form');
		setError('');
		setSuccess('');
		setQrCode('');
		setTwoFactorSecret('');
		setSlideDirection('');
		setDetectedMemberInstitute(null);
		setJoinScenario(null);
		setLoading(false);
		setEmailValidationStatus('idle');
		setShowSuccessModal(false);
		setPasswordStepMode('setup');
		setTwoFAMode('setup');
		setShowAAFButton(false);
		if (emailValidationTimeout) {
			clearTimeout(emailValidationTimeout);
			setEmailValidationTimeout(null);
		}
	};

	// Enhanced reset function for modal close
	const handleModalClose = () => {
		console.log('=== MODAL CLOSE TRIGGERED ===');
		console.log('handleModalClose called at:', new Date().toISOString());
		console.log('Current state before reset:', {
			loginStep,
			joinStep,
			error,
			success,
			joinScenario,
			detectedMemberInstitute,
			emailValidationStatus,
		});
		resetForm();
		console.log('Form reset completed');
		onHide();
	};

	// Handle success modal close
	const handleSuccessModalClose = () => {
		setShowSuccessModal(false);
		// Ensure all state is properly reset
		setLoading(false);
		setError('');
		setSuccess('');
		onHide(); // Close the entire modal
	};

	const handleJoin = async (e) => {
		e.preventDefault();

		console.log('=== JOIN FORM SUBMISSION START ===');
		console.log('Current state:', {
			loading,
			emailValidationStatus,
			joinScenario,
			detectedMemberInstitute,
			formData,
			showSuccessModal,
		});

		// Prevent multiple submissions
		if (loading) {
			console.log('Form is already loading, preventing submission');
			return;
		}

		// Set loading state AFTER preventing multiple submissions
		// This prevents the loading state from interfering with the click event
		setLoading(true);
		setError('');

		try {
			// Check if email validation has completed
			if (emailValidationStatus !== 'validated') {
				console.log(
					'Email validation not complete, status:',
					emailValidationStatus
				);
				setError(
					'Please wait for email validation to complete before submitting.'
				);
				setLoading(false);
				return;
			}

			// Check if email already exists
			if (
				joinScenario === 'email_exists_aaf' ||
				joinScenario === 'email_exists_regular'
			) {
				console.log('Email already exists, preventing join submission');
				setLoading(false);
				return;
			}

			// Allow AAF members to join (they will be created with AAF Member type)
			if (joinScenario === 'aaf_member') {
				console.log('AAF member detected, allowing join with AAF Member type');
				// Continue with the join process for AAF members
			}

			// Ensure member institute is detected for active institutes
			if (
				joinScenario === 'active_institute' &&
				!formData.member_institute_id
			) {
				console.log('Active institute but no member_institute_id');
				setError(
					'Please enter your email address to detect your member institute.'
				);
				setLoading(false);
				return;
			}

			// Validate required fields
			if (
				!formData.first_name ||
				!formData.last_name ||
				!formData.job_title ||
				!formData.email
			) {
				console.log('Required fields missing:', {
					first_name: !!formData.first_name,
					last_name: !!formData.last_name,
					job_title: !!formData.job_title,
					email: !!formData.email,
				});
				setError('Please fill in all required fields.');
				setLoading(false);
				return;
			}

			const joinData = {
				email: formData.email,
				first_name: formData.first_name,
				last_name: formData.last_name,
				job_title: formData.job_title,
				member_institute_id: parseInt(formData.member_institute_id) || null,
				membership_type:
					joinScenario === 'aaf_member'
						? 'AAF Member'
						: joinScenario === 'individual'
						? 'Individual Member'
						: 'Non AAF Member',
			};

			console.log(
				'All validations passed, submitting join request with data:',
				joinData
			);

			const response = await authAPI.requestJoin(joinData);
			console.log('Join request response:', response);

			// Show success modal instead of just setting success message
			setShowSuccessModal(true);
			console.log('Success modal set to true');

			// Reset form state after successful submission
			setFormData({
				email: '',
				first_name: '',
				last_name: '',
				member_institute_id: '',
			});
			setEmailValidationStatus('idle');
			setJoinScenario(null);
			setDetectedMemberInstitute(null);
		} catch (error) {
			console.error('Join request error:', error);
			setError(
				error.message || 'Failed to submit join request. Please try again.'
			);
		} finally {
			setLoading(false);
			console.log('=== JOIN FORM SUBMISSION END ===');
		}
	};

	// Step 1: Check user status after email entry
	const handleEmailSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		try {
			const response = await authAPI.checkUserStatus(formData.email);

			switch (response.status) {
				case 'aaf_member':
					setError(
						response.message +
							' Please use the Login via Institutional Credentials button to access the website.'
					);
					setShowAAFButton(true);
					break;
				case 'can_join':
					setError(
						'You can join WIL Australia through this member institute. Please use the Join Now option.'
					);
					setShowAAFButton(false);
					break;
				case 'inactive_institute':
					setError(response.message);
					setShowAAFButton(false);
					break;
				case 'individual_member':
					setError(
						"The email entered is not associated with an institutional WIL Australia membership, but you may join WIL Australia as an individual member. Please note that joining via your institution email will avoid cost, whereas individual membership (with non-institutional email) will incur a cost. Please press 'Individual Member Join Up' or alternatively enter an email from a Member Institute"
					);
					setShowAAFButton(false);
					break;
				case 'pending_approval':
					setError(response.message);
					setShowAAFButton(false);
					break;
				case 'account_blocked':
					setError(response.message);
					setShowAAFButton(false);
					break;
				case 'password_setup_required':
					// For new users, go to password step in setup mode
					setPasswordStepMode('setup');
					setTwoFAMode('setup');
					setShowAAFButton(false);
					slideToStep('password');
					break;
				case '2fa_setup_required':
					// For users with password but no 2FA, go to password step first (in setup mode)
					// They need to set up their password before setting up 2FA
					setPasswordStepMode('setup');
					setTwoFAMode('setup'); // Will be used after password setup
					setShowAAFButton(false);
					slideToStep('password');
					break;
				case 'ready_for_login':
					// For verified users with password, go to password step in entry mode
					setPasswordStepMode('entry');
					setShowAAFButton(false);
					slideToStep('password');
					break;
				default:
					setError('Unexpected user status. Please try again.');
					setShowAAFButton(false);
			}
		} catch (error) {
			setError(
				error.message || 'Failed to check user status. Please try again.'
			);
			setShowAAFButton(false);
		} finally {
			setLoading(false);
		}
	};

	// Step 2: Handle password step (setup or entry)
	const handlePasswordStep = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		console.log('=== HANDLE PASSWORD STEP START ===');
		console.log('Password step mode:', passwordStepMode);
		console.log('Form data:', formData);

		if (passwordStepMode === 'setup') {
			// Password setup mode
			if (formData.password !== formData.confirmPassword) {
				setError('Passwords do not match.');
				setLoading(false);
				return;
			}

			if (formData.password.length < 8) {
				setError('Password must be at least 8 characters long.');
				setLoading(false);
				return;
			}

			try {
				await authAPI.setupPassword(formData.email, formData.password);
				setSuccess('Password set up successfully!');

				// Move to 2FA step
				setTimeout(() => {
					slideToStep('2fa');
					setSuccess('');
				}, 1500);
			} catch (error) {
				setError(
					error.message || 'Failed to set up password. Please try again.'
				);
			} finally {
				setLoading(false);
			}
		} else {
			// Password entry mode (for existing users)
			try {
				console.log('About to call verifyPassword API...');
				// Verify password only (without attempting login)
				const passwordResponse = await authAPI.verifyPassword(
					formData.email,
					formData.password
				);
				console.log('verifyPassword API response:', passwordResponse);

				if (passwordResponse.message === 'Password verified successfully') {
					// Password verified, check if user has 2FA enabled
					const userStatus = await authAPI.checkUserStatus(formData.email);

					// Debug logging to see what's being returned
					console.log(
						'Password verification - User status response:',
						userStatus
					);
					console.log('User status:', userStatus.status);
					console.log('User object:', userStatus.user);
					console.log(
						'2FA enabled flag from verifyPassword:',
						passwordResponse.requires2FA
					);

					if (userStatus.status === '2fa_setup_required') {
						// User needs to set up 2FA
						console.log('Routing to 2FA setup');
						setTwoFAMode('setup');
						setTimeout(() => {
							slideToStep('2fa');
						}, 500);
					} else if (
						userStatus.status === 'ready_for_login' &&
						passwordResponse.requires2FA
					) {
						// User has 2FA enabled, go to 2FA entry
						console.log('Routing to 2FA entry (user has 2FA enabled)');
						setTwoFAMode('entry');
						setTimeout(() => {
							slideToStep('2fa');
						}, 500);
					} else if (userStatus.status === 'ready_for_login') {
						// User is ready for login without 2FA
						console.log(
							'User ready for login without 2FA - logging in directly'
						);
						// Now attempt actual login since password is verified
						const loginResponse = await authAPI.loginWithPassword(
							formData.email,
							formData.password
						);

						if (loginResponse.message === 'Login successful') {
							// Login successful, close modal and update context
							login(loginResponse.user, loginResponse.token);
							const firstName = loginResponse.user?.first_name || 'User';
							console.log('User data:', loginResponse.user);

							// Extract audience segment from token
							let audienceSegment = 'Academic Staff'; // Default
							try {
								// Use the token from localStorage (same one used for redirection)
								const storedToken = localStorage.getItem('authToken');
								console.log(
									'Using stored token for audience segment:',
									storedToken?.substring(0, 50) + '...'
								);

								if (storedToken) {
									const decodedToken = JSON.parse(
										atob(storedToken.split('.')[1])
									);
									console.log(
										'Decoded token for audience segment:',
										decodedToken
									);
									if (
										decodedToken.audience_segments &&
										decodedToken.audience_segments.length > 0
									) {
										const primarySegment = decodedToken.audience_segments[0];
										audienceSegment =
											primarySegment.name ||
											primarySegment.attributes?.name ||
											primarySegment;
									}
								} else {
									console.log('No stored token found, using default');
								}
							} catch (error) {
								console.log(
									'Error decoding token for audience segment:',
									error
								);
							}

							setSuccess(`Hi, ${firstName}!`);

							// Close modal after 2 seconds - context will handle redirect
							setTimeout(() => {
								handleModalClose();
							}, 2000);
						} else {
							setError(
								'Login failed after password verification. Please try again.'
							);
						}
					} else {
						// Handle other statuses
						console.log('Unexpected user status:', userStatus.status);
						setError('Unexpected user status. Please try again.');
					}
				} else {
					setError('Invalid password. Please try again.');
				}
			} catch (error) {
				setError(
					error.message || 'Password verification failed. Please try again.'
				);
			} finally {
				setLoading(false);
			}
		}
	};

	// Step 2: Setup password (legacy - keeping for now)
	const handlePasswordSetup = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		if (formData.password !== formData.confirmPassword) {
			setError('Passwords do not match.');
			setLoading(false);
			return;
		}

		if (formData.password.length < 8) {
			setError('Password must be at least 8 characters long.');
			setLoading(false);
			return;
		}

		try {
			await authAPI.setupPassword(formData.email, formData.password);
			setSuccess('Password set up successfully!');

			// Move to 2FA setup step
			setTimeout(() => {
				slideToStep('2fa');
				setSuccess('');
			}, 1500);
		} catch (error) {
			setError(error.message || 'Failed to set up password. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Step 3: Setup 2FA
	const handle2FASetup = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		try {
			const response = await authAPI.setup2FA(formData.email);
			setQrCode(response.qrCode);
			setTwoFactorSecret(response.secret);
			setSuccess(
				'2FA setup initiated. Please scan the QR code with your authenticator app.'
			);
		} catch (error) {
			setError(error.message || 'Failed to set up 2FA. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Step 4: Verify 2FA code (for setup)
	const handle2FAVerification = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		if (!formData.twoFactorCode || formData.twoFactorCode.length !== 6) {
			setError('Please enter a valid 6-digit 2FA code.');
			setLoading(false);
			return;
		}

		try {
			// Only verify 2FA - don't try to login here
			await authAPI.verify2FA(formData.email, formData.twoFactorCode);
			setSuccess('2FA setup completed successfully!');

			// After 2FA setup, trigger final login
			setTimeout(() => {
				handleFinalLogin();
			}, 1500);
		} catch (error) {
			setError(error.message || 'Failed to verify 2FA code. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Step 5: Final login after 2FA setup
	const handleFinalLogin = async () => {
		console.log('=== FINAL LOGIN DEBUG ===');
		console.log('Starting final login process...');
		console.log('Email:', formData.email);
		console.log('Password length:', formData.password?.length);
		console.log('2FA code:', formData.twoFactorCode);

		setLoading(true);
		setError('');

		try {
			// Login with password and 2FA code
			console.log('Calling loginWithPassword API...');
			const loginResponse = await authAPI.loginWithPassword(
				formData.email,
				formData.password,
				formData.twoFactorCode
			);

			console.log('Login response:', loginResponse);

			if (loginResponse.message === 'Login successful') {
				// Login successful, close modal and update context
				console.log('Login successful, updating context...');
				login(loginResponse.user, loginResponse.token);
				const firstName = loginResponse.user?.first_name || 'User';
				console.log('User data:', loginResponse.user);

				// Extract audience segment from token
				let audienceSegment = 'Academic Staff'; // Default
				try {
					// Use the token from localStorage (same one used for redirection)
					const storedToken = localStorage.getItem('authToken');
					console.log(
						'Using stored token for audience segment:',
						storedToken?.substring(0, 50) + '...'
					);

					if (storedToken) {
						const decodedToken = JSON.parse(atob(storedToken.split('.')[1]));
						if (
							decodedToken.audience_segments &&
							decodedToken.audience_segments.length > 0
						) {
							const primarySegment = decodedToken.audience_segments[0];
							audienceSegment =
								primarySegment.name ||
								primarySegment.attributes?.name ||
								primarySegment;
						}
					} else {
						console.log('No stored token found, using default');
					}
				} catch (error) {
					console.log('Error decoding token for audience segment:', error);
				}

				setSuccess(
					`Hi, ${firstName}!${
						audienceSegment ? ` Your view is ${audienceSegment}` : ''
					}`
				);

				// Close modal after 2 seconds and redirect to academics page
				setTimeout(() => {
					handleModalClose();
				}, 2000);
			} else {
				// Login failed after 2FA setup
				console.error('Login failed after 2FA setup:', loginResponse);
				setError(
					'2FA setup completed but login failed. Please try logging in again.'
				);

				// Reset to email step for fresh login attempt
				setTimeout(() => {
					slideToStep('email');
					setSuccess('');
					setError('');
				}, 3000);
			}
		} catch (loginError) {
			console.error('Login failed after 2FA setup:', loginError);
			setError(
				'2FA setup completed but login failed. Please try logging in again.'
			);

			// Reset to email step for fresh login attempt
			setTimeout(() => {
				slideToStep('email');
				setSuccess('');
				setError('');
			}, 3000);
		} finally {
			setLoading(false);
		}
	};

	// Step 4.5: Handle 2FA entry for existing users
	const handle2FAEntry = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		try {
			// Login with password and 2FA code
			const loginResponse = await authAPI.loginWithPassword(
				formData.email,
				formData.password,
				formData.twoFactorCode
			);

			if (loginResponse.message === 'Login successful') {
				// Login successful
				login(loginResponse.user, loginResponse.token);
				const firstName = loginResponse.user?.first_name || 'User';

				// Extract audience segment from token
				let audienceSegment = 'Academic Staff'; // Default
				try {
					// Use the token from localStorage (same one used for redirection)
					const storedToken = localStorage.getItem('authToken');
					console.log(
						'Using stored token for audience segment:',
						storedToken?.substring(0, 50) + '...'
					);

					if (storedToken) {
						const decodedToken = JSON.parse(atob(storedToken.split('.')[1]));
						if (
							decodedToken.audience_segments &&
							decodedToken.audience_segments.length > 0
						) {
							const primarySegment = decodedToken.audience_segments[0];
							audienceSegment =
								primarySegment.name ||
								primarySegment.attributes?.name ||
								primarySegment;
						}
					} else {
						console.log('No stored token found, using default');
					}
				} catch (error) {
					console.log('Error decoding token for audience segment:', error);
				}

				setSuccess(
					`Hi, ${firstName}!${
						audienceSegment ? ` Your view is ${audienceSegment}` : ''
					}`
				);

				// Close modal after 2 seconds and redirect to academics page
				setTimeout(() => {
					handleModalClose();
				}, 2000);
			} else {
				setError('Invalid 2FA code. Please try again.');
			}
		} catch (error) {
			setError(error.message || '2FA verification failed. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Step 4.6: Handle 2FA login (for users with password and 2FA)
	const handle2FALogin = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		try {
			// Verify password and 2FA code together
			const loginResponse = await authAPI.loginWithPassword(
				formData.email,
				formData.password,
				formData.twoFactorCode
			);

			console.log('Login response:', loginResponse);

			if (loginResponse.message === 'Login successful') {
				// Login successful
				login(loginResponse.user, loginResponse.token);
				const firstName = loginResponse.user?.first_name || 'User';

				// Extract audience segment from token
				let audienceSegment = 'Academic Staff'; // Default
				try {
					// Use the token from localStorage (same one used for redirection)
					const storedToken = localStorage.getItem('authToken');
					console.log(
						'Using stored token for audience segment:',
						storedToken?.substring(0, 50) + '...'
					);

					if (storedToken) {
						const decodedToken = JSON.parse(atob(storedToken.split('.')[1]));
						if (
							decodedToken.audience_segments &&
							decodedToken.audience_segments.length > 0
						) {
							const primarySegment = decodedToken.audience_segments[0];
							audienceSegment =
								primarySegment.name ||
								primarySegment.attributes?.name ||
								primarySegment;
						}
					} else {
						console.log('No stored token found, using default');
					}
				} catch (error) {
					console.log('Error decoding token for audience segment:', error);
				}

				setSuccess(
					`Hi, ${firstName}!${
						audienceSegment ? ` Your view is ${audienceSegment}` : ''
					}`
				);

				// Close modal after 2 seconds and redirect to academics page
				setTimeout(() => {
					handleModalClose();
				}, 2000);
			} else {
				setError('Invalid password or 2FA code. Please try again.');
			}
		} catch (error) {
			setError(error.message || '2FA verification failed. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Step 5: Login (legacy - for users without 2FA)
	const handleLogin = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		try {
			const response = await authAPI.loginWithPassword(
				formData.email,
				formData.password,
				formData.twoFactorCode || null
			);

			// Store token and user data
			login(response.user, response.token);
			const firstName = response.user?.first_name || 'User';

			// Extract audience segment from token
			let audienceSegment = 'Academic Staff'; // Default
			try {
				// Use the token from localStorage (same one used for redirection)
				const storedToken = localStorage.getItem('authToken');
				console.log(
					'Using stored token for audience segment:',
					storedToken?.substring(0, 50) + '...'
				);

				if (storedToken) {
					const decodedToken = JSON.parse(atob(storedToken.split('.')[1]));
					if (
						decodedToken.audience_segments &&
						decodedToken.audience_segments.length > 0
					) {
						const primarySegment = decodedToken.audience_segments[0];
						audienceSegment =
							primarySegment.name ||
							primarySegment.attributes?.name ||
							primarySegment;
					}
				} else {
					console.log('No stored token found, using default');
				}
			} catch (error) {
				console.log('Error decoding token for audience segment:', error);
			}

			setSuccess(
				`Hi, ${firstName}!${
					audienceSegment ? ` Your view is ${audienceSegment}` : ''
				}`
			);

			// Close modal after 2 seconds and redirect to academics page
			setTimeout(() => {
				handleModalClose();
			}, 2000);
		} catch (error) {
			setError(error.message || 'Login failed. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const getStepNumber = (step) => {
		const steps = {
			email: 1,
			password: 2, // Combined step for password setup/entry
			'2fa': 3, // Combined step for 2FA setup/entry
		};
		return steps[step] || 1;
	};

	const getCurrentStepNumber = () => getStepNumber(loginStep);

	const renderStepIndicator = () => {
		if (mode !== 'login') return null;

		const totalSteps = 3;
		const currentStep = getCurrentStepNumber();

		return (
			<StepIndicator>
				{Array.from({ length: totalSteps }, (_, i) => {
					const stepNum = i + 1;
					const isActive = stepNum === currentStep;
					const isCompleted = stepNum < currentStep;
					const isNext = stepNum === currentStep + 1;

					return (
						<React.Fragment key={stepNum}>
							<StepDot
								active={isActive}
								completed={isCompleted}
								title={`Step ${stepNum}`}
								brandColors={strapiAssetData.strapiAsset?.brand_colours}
							/>
							{stepNum < totalSteps && <StepLine completed={isCompleted} />}
						</React.Fragment>
					);
				})}
			</StepIndicator>
		);
	};

	const renderForm = () => {
		switch (mode) {
			case 'join':
				// Show success modal if join was successful
				if (showSuccessModal) {
					return (
						<FormContainer>
							<FormTitle fontFamily={strapiAssetData.strapiAsset?.font_family}>
								Join Request Submitted
							</FormTitle>
							<FormSubtitle
								fontFamily={strapiAssetData.strapiAsset?.font_family}
							>
								Your join request has been submitted successfully!
							</FormSubtitle>

							<div className='text-center my-4'>
								<div className='mb-4'>
									<i
										className='fas fa-check-circle text-success'
										style={{ fontSize: '3rem' }}
									></i>
								</div>
								<p className='text-muted mb-4'>
									Please check your inbox for a verification email.
								</p>
								<StyledButton
									type='button'
									variant='primary'
									onClick={handleSuccessModalClose}
									className='px-5'
									brandColors={strapiAssetData.strapiAsset?.brand_colours}
									buttonShape={strapiAssetData.strapiAsset?.button_shape}
								>
									OK
								</StyledButton>
							</div>
						</FormContainer>
					);
				}

				// Show join form
				return (
					<FormContainer>
						<FormTitle fontFamily={strapiAssetData.strapiAsset?.font_family}>
							Join WIL Australia
						</FormTitle>
						<FormSubtitle fontFamily={strapiAssetData.strapiAsset?.font_family}>
							Create your account to access exclusive resources and connect with
							the community.
						</FormSubtitle>

						<StyledForm
							onSubmit={handleJoin}
							brandColors={strapiAssetData.strapiAsset?.brand_colours}
						>
							{/* First Name and Last Name in one row */}
							<div className='row g-3 mb-3 name-row'>
								<div className='col-md-6'>
									<Form.Group>
										<Form.Label>First Name *</Form.Label>
										<Form.Control
											type='text'
											name='first_name'
											value={formData.first_name}
											onChange={handleInputChange}
											required
										/>
									</Form.Group>
								</div>
								<div className='col-md-6'>
									<Form.Group>
										<Form.Label>Last Name *</Form.Label>
										<Form.Control
											type='text'
											name='last_name'
											value={formData.last_name}
											onChange={handleInputChange}
											required
										/>
									</Form.Group>
								</div>
							</div>

							{/* Job Title */}
							<div className='row'>
								<div className='col-12'>
									<Form.Group className='mb-3'>
										<Form.Label>Job Title *</Form.Label>
										<Form.Control
											type='text'
											name='job_title'
											value={formData.job_title}
											onChange={handleInputChange}
											required
											placeholder='Enter your job title'
										/>
									</Form.Group>
								</div>
							</div>

							{/* Email Address */}
							<div className='row'>
								<div className='col-12'>
									<Form.Group className='mb-3'>
										<Form.Label>Email Address *</Form.Label>
										<div className='position-relative'>
											<Form.Control
												type='email'
												name='email'
												value={formData.email}
												onChange={handleInputChange}
												onBlur={handleEmailBlur}
												required
											/>
											{/* Email validation status indicator */}
											{emailValidationStatus === 'loading' && (
												<div className='position-absolute top-50 end-0 translate-middle-y me-3'>
													<Spinner
														animation='border'
														size='sm'
														className='text-primary'
													/>
												</div>
											)}
											{emailValidationStatus === 'validated' && (
												<div className='position-absolute top-50 end-0 translate-middle-y me-3'>
													<i
														className='fas fa-check-circle text-success'
														style={{ fontSize: '1.2rem' }}
													></i>
												</div>
											)}
										</div>
									</Form.Group>
								</div>
							</div>

							{/* Show detected member institute info */}
							{detectedMemberInstitute && joinScenario === 'aaf_member' && (
								<Alert variant='info' className='mb-3'>
									<strong>AAF Member Institute Detected</strong>
									<br />
									Your email is associated with{' '}
									<strong>{detectedMemberInstitute.name}</strong> which is an
									AAF member institution. Click "Verify Email" to create your
									account and then once your email is verified WIL Australia
									board will review your application.
								</Alert>
							)}

							{/* Show email already exists messages */}
							{joinScenario === 'email_exists_aaf' && (
								<Alert variant='warning' className='mb-3'>
									<strong>Email Already Exists</strong>
									<br />
									This email is already registered with an AAF member
									institution. Please use the{' '}
									<strong>Login via Institutional Credentials</strong> button to
									access the website.
								</Alert>
							)}

							{joinScenario === 'email_exists_regular' && (
								<Alert variant='warning' className='mb-3'>
									<strong>Email Already Exists</strong>
									<br />
									This email is already registered. Please use the{' '}
									<strong>Login</strong> option instead.
								</Alert>
							)}

							{detectedMemberInstitute &&
								joinScenario === 'active_institute' && (
									<Alert variant='info' className='mb-3'>
										<strong>Member Institute Detected</strong>
										<br />
										Your email is associated with{' '}
										<strong>{detectedMemberInstitute.name}</strong> who are an
										active WIL Australia member institution. Please click Verify
										Email button to proceed with the join process.
									</Alert>
								)}

							{detectedMemberInstitute &&
								joinScenario === 'inactive_institute' && (
									<Alert variant='warning' className='mb-3'>
										<strong>Inactive Member Institute</strong>
										<br />
										Your email is associated with{' '}
										<strong>{detectedMemberInstitute.name}</strong> whose WIL
										Australia membership is not active. Please contact your
										institution's WIL Australia representative.
									</Alert>
								)}

							{joinScenario === 'individual' && (
								<Alert variant='info' className='mb-3'>
									<strong>Institute Membership does not exist</strong>
									<br />
									The email entered is not associated with an institutional WIL
									Australia membership, but you may join WIL Australia as an
									individual member. Please note that joining via your
									institution email will avoid cost, whereas individual
									membership (with non-institutional email) will incur a cost.
									Please press 'Individual Member Join Up' or alternatively
									enter an email from a Member Institute
								</Alert>
							)}

							{/* Member Institute is automatically detected and displayed in the message above */}
							{/* Hidden input to maintain form validation */}
							<input
								type='hidden'
								name='member_institute_id'
								value={formData.member_institute_id}
							/>

							{/* Action Buttons - Different based on scenario */}
							<div className='text-center'>
								{joinScenario === 'email_exists_aaf' ? (
									<div>
										<StyledButton
											type='button'
											variant='primary'
											onClick={() => {
												// Redirect to AAF login with returnUrl parameter
												const currentUrl = window.location.href;
												const aafUrl = new URL(
													process.env.GATSBY_AAF_LOGIN_URL
												);
												aafUrl.searchParams.set('returnUrl', currentUrl);
												if (process.env.GATSBY_AAF_LOGIN_URL) {
													window.location.href = aafUrl.toString();
												} else {
													setError('AAF login URL not configured');
												}
											}}
											className='w-100'
											style={{ border: '2px solid' }}
											brandColors={strapiAssetData.strapiAsset?.brand_colours}
											buttonShape={strapiAssetData.strapiAsset?.button_shape}
										>
											Login via AAF using University Credentials
										</StyledButton>
									</div>
								) : joinScenario === 'email_exists_regular' ? (
									<div className='row g-3'>
										<div className='col-6'>
											<StyledButton
												type='button'
												variant='outline-secondary'
												onClick={handleModalClose}
												className='w-100'
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												Cancel
											</StyledButton>
										</div>
										<div className='col-6'>
											<StyledButton
												type='button'
												variant='primary'
												onClick={() => {
													// Clear all state explicitly
													setError('');
													setSuccess('');
													setJoinScenario('');
													setDetectedMemberInstitute(null);
													setEmailValidationStatus('');

													// Clear the join form and close modal
													resetForm();
													onHide();

													// Small delay to ensure modal closes before opening new one
													setTimeout(() => {
														// Trigger login modal by dispatching a custom event
														if (typeof window !== 'undefined') {
															const event = new CustomEvent('openLoginModal', {
																detail: { type: 'login' },
															});
															window.dispatchEvent(event);
														}
													}, 100);
												}}
												className='w-100'
												style={{ border: '2px solid' }}
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												Login via website using Credentials you set-up
											</StyledButton>
										</div>
									</div>
								) : joinScenario === 'aaf_member' && detectedMemberInstitute ? (
									<div className='row g-3'>
										<div className='col-6'>
											<StyledButton
												type='button'
												variant='outline-secondary'
												onClick={handleModalClose}
												className='w-100'
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												Cancel
											</StyledButton>
										</div>
										<div className='col-6'>
											<StyledButton
												type='submit'
												variant='primary'
												className='w-100'
												disabled={loading}
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												{loading ? 'Verifying...' : 'Verify Email'}
											</StyledButton>
										</div>
									</div>
								) : joinScenario === 'active_institute' &&
								  detectedMemberInstitute ? (
									<div className='row g-3'>
										<div className='col-6'>
											<StyledButton
												type='button'
												variant='outline-secondary'
												onClick={handleModalClose}
												className='w-100'
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												Cancel Join
											</StyledButton>
										</div>
										<div className='col-6'>
											<StyledButton
												type='button'
												variant='primary'
												disabled={
													loading || emailValidationStatus !== 'validated'
												}
												className='w-100'
												onClick={(e) => {
													// Simple click handler - no complex logging
													if (
														!loading &&
														emailValidationStatus === 'validated'
													) {
														handleJoin({ preventDefault: () => {} });
													}
												}}
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												{loading ? (
													<>
														<Spinner
															animation='border'
															size='sm'
															className='me-2'
														/>
														Verify Email
													</>
												) : emailValidationStatus !== 'validated' ? (
													'Waiting for validation...'
												) : (
													'Verify Email'
												)}
											</StyledButton>
										</div>
									</div>
								) : joinScenario === 'inactive_institute' ? (
									<div className='row g-3'>
										<div className='col-6'>
											<StyledButton
												type='button'
												variant='outline-secondary'
												onClick={handleModalClose}
												className='w-100'
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												Cancel Join
											</StyledButton>
										</div>
										<div className='col-6'>
											<StyledButton
												type='button'
												variant='warning'
												onClick={handleModalClose}
												className='w-100'
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												Notify WIL Representative
											</StyledButton>
										</div>
									</div>
								) : joinScenario === 'individual' ? (
									<div>
										<StyledButton
											type='submit'
											variant='primary'
											className='w-100'
											disabled={loading}
											style={{ border: '2px solid' }}
											brandColors={strapiAssetData.strapiAsset?.brand_colours}
											buttonShape={strapiAssetData.strapiAsset?.button_shape}
										>
											{loading ? 'Verifying...' : 'Individual Member Join Up'}
										</StyledButton>
									</div>
								) : null}
							</div>
						</StyledForm>
					</FormContainer>
				);

			case 'login':
				return (
					<StepContainer>
						{renderStepIndicator()}

						<StepContent slideDirection={slideDirection}>
							{loginStep === 'email' && (
								<FormContainer>
									<FormTitle
										fontFamily={strapiAssetData.strapiAsset?.font_family}
									>
										Welcome Back
									</FormTitle>
									<FormSubtitle
										fontFamily={strapiAssetData.strapiAsset?.font_family}
									>
										Enter your email address to continue
									</FormSubtitle>

									<StyledForm
										onSubmit={handleEmailSubmit}
										brandColors={strapiAssetData.strapiAsset?.brand_colours}
									>
										<Form.Group className='mb-4'>
											<Form.Label>Email Address *</Form.Label>
											<Form.Control
												type='email'
												name='email'
												value={formData.email}
												onChange={handleInputChange}
												required
												placeholder='Enter your email address'
											/>
										</Form.Group>

										<div className='text-center'>
											<StyledButton
												type='submit'
												variant='primary'
												disabled={loading}
												className='mb-3'
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												{loading ? (
													<>
														<Spinner
															animation='border'
															size='sm'
															className='me-2'
														/>
														Checking...
													</>
												) : (
													'Continue'
												)}
											</StyledButton>
										</div>
									</StyledForm>
								</FormContainer>
							)}
							{loginStep === 'password' && (
								<FormContainer>
									<FormTitle>
										{passwordStepMode === 'setup'
											? 'Set Up Your Password'
											: 'Enter Your Password'}
									</FormTitle>
									<FormSubtitle>
										{passwordStepMode === 'setup'
											? 'Create a secure password for your account'
											: 'Please enter your password to continue'}
									</FormSubtitle>

									<StyledForm onSubmit={handlePasswordStep}>
										<Form.Group className='mb-3'>
											<Form.Label>Password *</Form.Label>
											<Form.Control
												type='password'
												name='password'
												value={formData.password}
												onChange={handleInputChange}
												required
												minLength={8}
												placeholder={
													passwordStepMode === 'setup'
														? 'Enter your new password'
														: 'Enter your password'
												}
											/>
											{passwordStepMode === 'setup' && (
												<Form.Text>
													Password must be at least 8 characters long.
												</Form.Text>
											)}
											{passwordStepMode === 'entry' && (
												<div className='text-start mt-2'>
													<a
														href='/password-reset'
														style={{
															color:
																strapiAssetData.strapiAsset?.brand_colours
																	?.secondary_1 || 'var(--secondary_1)',
															textDecoration: 'none',
															fontSize: '0.9rem',
														}}
														onMouseEnter={(e) => {
															e.target.style.textDecoration = 'underline';
														}}
														onMouseLeave={(e) => {
															e.target.style.textDecoration = 'none';
														}}
													>
														Forgot your password?
													</a>
												</div>
											)}
										</Form.Group>

										{passwordStepMode === 'setup' && (
											<Form.Group className='mb-4'>
												<Form.Label>Confirm Password *</Form.Label>
												<Form.Control
													type='password'
													name='confirmPassword'
													value={formData.confirmPassword}
													onChange={handleInputChange}
													required
													minLength={8}
													placeholder='Confirm your new password'
												/>
											</Form.Group>
										)}

										<div className='text-center'>
											<StyledButton
												type='submit'
												variant='primary'
												disabled={loading}
												className='mb-3'
												brandColors={strapiAssetData.strapiAsset?.brand_colours}
												buttonShape={strapiAssetData.strapiAsset?.button_shape}
											>
												{loading ? (
													<>
														<Spinner
															animation='border'
															size='sm'
															className='me-2'
														/>
														{passwordStepMode === 'setup'
															? 'Setting Password...'
															: 'Verifying...'}
													</>
												) : passwordStepMode === 'setup' ? (
													'Set Password'
												) : (
													'Continue'
												)}
											</StyledButton>
										</div>
									</StyledForm>
								</FormContainer>
							)}
							{loginStep === '2fa' && (
								<FormContainer>
									<FormTitle>
										{twoFAMode === 'setup'
											? 'Two-Factor Authentication Setup'
											: 'Two-Factor Authentication'}
									</FormTitle>
									<FormSubtitle>
										{twoFAMode === 'setup'
											? "We'll generate a QR code for you to scan with your authenticator app."
											: 'Please enter your 2FA code to continue'}
									</FormSubtitle>

									<StyledForm
										onSubmit={
											twoFAMode === 'setup'
												? !qrCode
													? handle2FASetup
													: handle2FAVerification
												: handle2FAEntry
										}
									>
										{twoFAMode === 'setup' ? (
											// 2FA Setup Mode
											!qrCode ? (
												<div className='text-center'>
													<StyledButton
														type='submit'
														variant='primary'
														disabled={loading}
														brandColors={
															strapiAssetData.strapiAsset?.brand_colours
														}
														buttonShape={
															strapiAssetData.strapiAsset?.button_shape
														}
													>
														{loading ? (
															<>
																<Spinner
																	animation='border'
																	size='sm'
																	className='me-2'
																/>
																Generating QR Code...
															</>
														) : (
															'Generate QR Code'
														)}
													</StyledButton>
												</div>
											) : (
												<>
													<QRCodeContainer>
														<img src={qrCode} alt='QR Code' />
														{twoFactorSecret && (
															<SecretCode>
																<small className='text-muted'>
																	Manual code:{' '}
																</small>
																<code>{twoFactorSecret}</code>
															</SecretCode>
														)}
													</QRCodeContainer>

													<Form.Group className='mb-4'>
														<Form.Label>2FA Code *</Form.Label>
														<Form.Control
															type='text'
															name='twoFactorCode'
															value={formData.twoFactorCode}
															onChange={handleInputChange}
															required
															maxLength={6}
															placeholder='Enter 6-digit code from your app'
														/>
														<Form.Text>
															Enter the 6-digit code from your authenticator
															app.
														</Form.Text>
													</Form.Group>

													<div className='text-center'>
														<StyledButton
															type='submit'
															variant='primary'
															disabled={loading}
															brandColors={
																strapiAssetData.strapiAsset?.brand_colours
															}
															buttonShape={
																strapiAssetData.strapiAsset?.button_shape
															}
														>
															{loading ? (
																<>
																	<Spinner
																		animation='border'
																		size='sm'
																		className='me-2'
																	/>
																	Verifying...
																</>
															) : (
																'Verify & Complete Setup'
															)}
														</StyledButton>
													</div>
												</>
											)
										) : (
											// 2FA Entry Mode (for existing users)
											<>
												<Form.Group className='mb-4'>
													<Form.Label>2FA Code *</Form.Label>
													<Form.Control
														type='text'
														name='twoFactorCode'
														value={formData.twoFactorCode}
														onChange={handleInputChange}
														required
														maxLength={6}
														placeholder='Enter 6-digit code from your app'
													/>
													<div className='mt-2'>
														<a
															href='/password-reset'
															style={{
																color:
																	strapiAssetData.strapiAsset?.brand_colours
																		?.secondary_1 || 'var(--secondary_1)',
																textDecoration: 'none',
																fontSize: '0.9rem',
															}}
															onMouseEnter={(e) => {
																e.target.style.textDecoration = 'underline';
															}}
															onMouseLeave={(e) => {
																e.target.style.textDecoration = 'none';
															}}
														>
															Forgot Your MFA Code?
														</a>
													</div>
												</Form.Group>

												<div className='text-center'>
													<StyledButton
														type='submit'
														variant='primary'
														disabled={loading}
														brandColors={
															strapiAssetData.strapiAsset?.brand_colours
														}
														buttonShape={
															strapiAssetData.strapiAsset?.button_shape
														}
													>
														{loading ? (
															<>
																<Spinner
																	animation='border'
																	size='sm'
																	className='me-2'
																/>
																Logging in...
															</>
														) : (
															'Login'
														)}
													</StyledButton>
												</div>
											</>
										)}
									</StyledForm>
								</FormContainer>
							)}
						</StepContent>
					</StepContainer>
				);

			default:
				return null;
		}
	};

	const getModalTitle = () => {
		if (mode === 'join') return 'Join WIL Australia';
		if (mode === 'login') {
			switch (loginStep) {
				case 'email':
					return 'Welcome Back';
				case 'password':
					return passwordStepMode === 'setup'
						? 'Set Up Password'
						: 'Enter Password';
				case '2fa':
					return twoFAMode === 'setup'
						? 'Two-Factor Authentication Setup'
						: 'Two-Factor Authentication';
				default:
					return 'Login';
			}
		}
		return 'Authentication';
	};

	return (
		<StyledModal
			show={show}
			onHide={handleModalClose}
			size='lg'
			centered
			backdrop={true}
			onBackdropClick={handleModalClose}
			brandColors={strapiAssetData.strapiAsset?.brand_colours}
		>
			<Modal.Header closeButton>
				<Modal.Title>{getModalTitle()}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{error && (
					<>
						<AlertStyled variant='danger'>{error}</AlertStyled>
						{error.includes('individual member') && (
							<div className='text-center mt-3 mb-3'>
								<StyledButton
									type='button'
									variant='primary'
									onClick={() => {
										// Clear error and close login modal
										setError('');
										onHide();

										// Small delay to ensure modal closes before opening new one
										setTimeout(() => {
											// Trigger join modal by dispatching a custom event
											if (typeof window !== 'undefined') {
												const event = new CustomEvent('openLoginModal', {
													detail: {
														type: 'join',
														prefilledEmail: formData.email,
													},
												});
												window.dispatchEvent(event);
											}
										}, 100);
									}}
									style={{ border: '2px solid' }}
									brandColors={strapiAssetData.strapiAsset?.brand_colours}
									buttonShape={strapiAssetData.strapiAsset?.button_shape}
								>
									Individual Member Join Up
								</StyledButton>
							</div>
						)}
						{showAAFButton && (
							<div className='text-center mt-3 mb-3'>
								<StyledButton
									type='button'
									variant='primary'
									onClick={() => {
										// Redirect to AAF login with returnUrl parameter
										const currentUrl = window.location.href;
										const aafUrl = new URL(process.env.GATSBY_AAF_LOGIN_URL);
										aafUrl.searchParams.set('returnUrl', currentUrl);
										if (process.env.GATSBY_AAF_LOGIN_URL) {
											window.location.href = aafUrl.toString();
										} else {
											setError('AAF login URL not configured');
										}
									}}
									style={{ border: '2px solid' }}
									brandColors={strapiAssetData.strapiAsset?.brand_colours}
									buttonShape={strapiAssetData.strapiAsset?.button_shape}
								>
									Login via AAF using University Credentials
								</StyledButton>
							</div>
						)}
					</>
				)}
				{renderForm()}
			</Modal.Body>
		</StyledModal>
	);
};

export default AuthModal;
